import React, { useState, useEffect, useRef } from 'react';
import { Mic, Send, LogOut, User } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const VoiceInterface = () => {
  const { getAuthHeaders, logout, userName } = useAuth();
  const navigate = useNavigate();
  const [voiceState, setVoiceState] = useState('idle'); // idle, listening, processing, speaking
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState('');
  const [voiceMode, setVoiceMode] = useState('tap'); // 'tap' or 'push'
  const [isConnected, setIsConnected] = useState(false);
  const [showTranscript, setShowTranscript] = useState(true);
  const [apiKeyConfigured, setApiKeyConfigured] = useState(null);
  const [hasShownError, setHasShownError] = useState(false);
  const [branding, setBranding] = useState({ company_name: 'AI Voice', logo_url: '' });
  const [processedResponseIds, setProcessedResponseIds] = useState(new Set());
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false); // Track if waiting for AI response
  const isProcessingRef = useRef(false); // Prevent duplicate requests
  const intentionallyStoppedRef = useRef(false); // Track if microphone was intentionally stopped (don't auto-restart)
  
  const peerConnectionRef = useRef(null);
  const dataChannelRef = useRef(null);
  const audioElementRef = useRef(null);
  const messagesEndRef = useRef(null);
  const isPushingRef = useRef(false);
  const localStreamRef = useRef(null);
  const geminiWebSocketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const speechRecognitionRef = useRef(null);
  const speechSynthesisRef = useRef(null);
  const audioUnlockedRef = useRef(false); // Track if audio is unlocked for iOS Safari
  const ttsQueueRef = useRef([]); // Queue for TTS on iOS Safari (to maintain user interaction context)
  const userInteractionHandlerRef = useRef(null); // Store user interaction handler for iOS Safari
  // Refs for managing transcription state across recognition sessions
  const finalTranscriptRef = useRef('');
  const interimTranscriptRef = useRef('');
  const lastCommittedFinalRef = useRef('');
  const sendTimeoutRef = useRef(null);
  const hasScheduledSendRef = useRef(false);
  const hasSentRef = useRef(false);
  const lastSentTranscriptRef = useRef(''); // Track last sent transcript to prevent Android duplicates
  const lastResultTimeRef = useRef(null); // Track when we last received a result (for silence detection)
  const silenceTimeoutRef = useRef(null); // Timeout to detect silence
  const lastDisplayedContentRef = useRef(''); // Track last displayed content to prevent UI duplicates
  const lastProcessedResultRef = useRef(''); // Track last processed result to prevent duplicate onresult events
  const messageUpdatedInThisEventRef = useRef(false); // Track if we updated message in this onresult event

  const handleLogout = () => {
    logout();
    navigate('/login');
    toast.success('Logged out successfully');
  };

  // Text-to-Speech function for AI responses
  const speakText = (text) => {
    console.log('speakText called with:', { textLength: text?.length, hasSpeechSynthesis: 'speechSynthesis' in window });
    if (!text || !('speechSynthesis' in window)) {
      console.warn('speakText: Early return - no text or no speechSynthesis', { text: !!text, speechSynthesis: 'speechSynthesis' in window });
      return;
    }

    // Check if document is visible (Safari may block TTS if tab is hidden)
    if (document.visibilityState === 'hidden') {
      console.warn('Document is hidden - TTS may not work on Safari');
      // Continue anyway - user might have switched tabs temporarily
    }

    // Clean text for speech (remove URLs, markdown, special characters)
    let cleanText = text
      .replace(/https?:\/\/[^\s]+/g, '') // Remove URLs
      .replace(/\[.*?\]/g, '') // Remove brackets and content
      .replace(/\*\*([^*]+)\*\*/g, '$1') // Remove bold markdown
      .replace(/\*([^*]+)\*/g, '$1') // Remove italic markdown
      .replace(/#+\s*/g, '') // Remove markdown headers
      .replace(/\n{3,}/g, '\n\n') // Remove excessive newlines
      .replace(/\bAED\b/gi, 'A E D') // Replace AED with "A E D" for better TTS pronunciation
      .trim();

    console.log('speakText: After cleaning', { originalLength: text?.length, cleanLength: cleanText?.length, cleanText: cleanText?.substring(0, 50) });

    if (!cleanText) {
      console.warn('speakText: Clean text is empty after cleaning, returning early');
      return;
    }

    // Detect iOS Safari for special handling
    const userAgent = navigator.userAgent || '';
    const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

    // Cancel any ongoing speech - CRITICAL for Safari
    // Safari requires cancel() to be called before new speech
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      console.log('speakText: Canceling existing speech before starting new');
      window.speechSynthesis.cancel();
      // Safari needs a small delay after cancel before new speech
      if (isIOSSafari || isSafari) {
        console.log('speakText: Safari detected, using setTimeout delay');
        setTimeout(() => {
          console.log('speakText: Starting speaking process after Safari delay');
          startSpeakingProcess(cleanText, isIOSSafari || isSafari);
        }, 100);
        return;
      }
      // For Chrome/other browsers, small delay after cancel helps too
      setTimeout(() => {
        console.log('speakText: Starting speaking process after cancel (Chrome/other)');
        startSpeakingProcess(cleanText, isIOSSafari || isSafari);
      }, 50);
      return;
    }

    console.log('speakText: No existing speech, starting immediately');
    startSpeakingProcess(cleanText, isIOSSafari || isSafari);
  };

  // Separate function to start speaking (helps with Safari timing)
  // NewAIVoice pattern: iOS Safari pause/resume workaround included
  const startSpeakingProcess = (cleanText, isSafariBrowser) => {

    // Split long text into chunks (TTS has limitations on text length)
    const maxChunkLength = 200; // Reasonable chunk size for TTS
    const sentences = cleanText.match(/[^.!?]+[.!?]+/g) || [cleanText];
    const chunks = [];
    let currentChunk = '';
    
    for (const sentence of sentences) {
      if ((currentChunk + sentence).length <= maxChunkLength) {
        currentChunk += sentence;
      } else {
        if (currentChunk) {
          chunks.push(currentChunk.trim());
        }
        // If single sentence is too long, split by commas or just truncate
        if (sentence.length > maxChunkLength) {
          const parts = sentence.match(/.{1,200}[,.]?/g) || [sentence];
          chunks.push(...parts.map(p => p.trim()));
        } else {
          currentChunk = sentence;
        }
      }
    }
    if (currentChunk) {
      chunks.push(currentChunk.trim());
    }

    // Function to get voices and start speaking
    const startSpeaking = () => {
      // Get available voices (try multiple times if needed)
      let voices = window.speechSynthesis.getVoices();
      console.log('Available TTS voices:', voices.length, isSafariBrowser ? '(Safari)' : '');
      
      // CRITICAL: If no voices, try to load them by triggering onvoiceschanged
      if (voices.length === 0) {
        console.log('No voices found, attempting to load voices...');
        // Trigger voice loading by calling getVoices() again
        // Some browsers need this to load voices
        const loadVoices = () => {
          voices = window.speechSynthesis.getVoices();
          console.log('Voices loaded after trigger:', voices.length);
          if (voices.length > 0) {
            speakWithVoices(voices);
          } else {
            // Even if no voices, try to speak with default (browser will use default)
            console.warn('No voices available, attempting to use browser default voice');
            speakWithVoices([]);
          }
        };
        
        // Try immediate reload
        setTimeout(() => {
          voices = window.speechSynthesis.getVoices();
          if (voices.length > 0) {
            console.log('Voices found on immediate reload:', voices.length);
            speakWithVoices(voices);
          } else {
            // Wait a bit longer and try again
            const waitTime = isSafariBrowser ? 500 : 300;
            setTimeout(loadVoices, waitTime);
          }
        }, 100);
        return;
      }
      
      // Voices are available, start speaking
      console.log('Voices available, starting TTS with', voices.length, 'voices');
      speakWithVoices(voices);
    };

    // Function to select voice and speak chunks
    const speakWithVoices = (voices) => {
      // Prefer male voices: Google US Male, Microsoft David, or other male voices
      // On iOS Safari, prefer native voices
      const preferredVoice = voices.length > 0 ? (
        isSafariBrowser ? (
          // On Safari, prefer native iOS voices first
          voices.find(voice => 
            (voice.name.includes('Samantha') || voice.name.includes('Alex') || voice.name.includes('Daniel')) &&
            voice.lang.startsWith('en')
          ) || voices.find(voice => voice.lang.startsWith('en'))
        ) : (
          // On other browsers, prefer cloud voices
          voices.find(voice => 
            (voice.name.includes('Google') && voice.name.includes('Male')) ||
            (voice.name.includes('Microsoft') && (voice.name.includes('David') || voice.name.includes('Mark'))) ||
            (voice.name.includes('Natural') && voice.name.includes('Male')) ||
            (voice.name.includes('Male') && voice.lang.startsWith('en'))
          ) || voices.find(voice => 
            voice.name.includes('Google') || 
            voice.name.includes('Natural') ||
            voice.name.includes('Enhanced') ||
            (voice.lang.startsWith('en') && voice.localService === false)
          ) || voices.find(voice => voice.lang.startsWith('en'))
        )
      ) : null;

      if (!preferredVoice && voices.length > 0) {
        console.warn('Preferred voice not found, using first available voice:', voices[0].name);
      }

      // Set voice state to speaking
      setVoiceState('speaking');

      // Function to speak a single chunk
      const speakChunk = (chunkIndex) => {
        if (chunkIndex >= chunks.length) {
          speechSynthesisRef.current = null;
          setVoiceState('idle');
          return;
        }

        const chunk = chunks[chunkIndex];
        if (!chunk) {
          speakChunk(chunkIndex + 1);
          return;
        }

        const utterance = new SpeechSynthesisUtterance(chunk);
        utterance.rate = 1.0;
        utterance.pitch = 1.0;
        utterance.volume = 1.0;
        utterance.lang = 'en-US';
        
        if (preferredVoice) {
          utterance.voice = preferredVoice;
        }

        utterance.onstart = () => {
          console.log(`TTS started speaking chunk ${chunkIndex + 1}/${chunks.length}`, isSafariBrowser ? '(Safari)' : '');
          speechSynthesisRef.current = utterance;
          // Clear any timeout that was checking if TTS started
          if (window.ttsStartTimeout) {
            clearTimeout(window.ttsStartTimeout);
            window.ttsStartTimeout = null;
          }
        };

        utterance.onend = () => {
          console.log(`TTS finished chunk ${chunkIndex + 1}/${chunks.length}`);
          speakChunk(chunkIndex + 1);
        };

        utterance.onerror = (event) => {
          console.error(`TTS error in chunk ${chunkIndex + 1}:`, event);
          console.error('TTS error details:', {
            error: event.error,
            charIndex: event.charIndex,
            charLength: event.charLength,
            chunk: chunk.substring(0, 100),
            totalChunks: chunks.length,
            voice: preferredVoice?.name || 'default',
            isSafari: isSafariBrowser
          });
          
          if (event.error !== 'interrupted') {
            console.warn(`TTS synthesis failed for chunk ${chunkIndex + 1} - continuing...`);
          }
          
          speakChunk(chunkIndex + 1);
        };

        try {
          console.log(`Speaking chunk ${chunkIndex + 1}/${chunks.length}: "${chunk.substring(0, 50)}..."`, { voice: preferredVoice?.name || 'default' });
          
          // Safari-specific: NewAIVoice pattern with iOS pause/resume workaround
          if (isSafariBrowser) {
            if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
              window.speechSynthesis.cancel();
              // NewAIVoice pattern: 100ms delay after cancel for iOS Safari
              setTimeout(() => {
                console.log('Safari: Calling speechSynthesis.speak after cancel');
                window.speechSynthesis.speak(utterance);
                
                // NewAIVoice pattern: iOS Safari pause/resume workaround
                // iOS Safari can pause speech, need to resume it
                const iosResumeInterval = setInterval(() => {
                  if (!window.speechSynthesis.speaking) {
                    clearInterval(iosResumeInterval);
                  } else if (window.speechSynthesis.paused) {
                    window.speechSynthesis.resume();
                    console.log('iOS Safari: Resumed paused speech');
                  }
                }, 300);
                
                // Clear interval after max duration (60 seconds)
                setTimeout(() => clearInterval(iosResumeInterval), 60000);
              }, 100); // NewAIVoice uses 100ms delay
            } else {
              console.log('Safari: Calling speechSynthesis.speak directly');
              window.speechSynthesis.speak(utterance);
              
              // NewAIVoice pattern: iOS Safari pause/resume workaround
              const iosResumeInterval = setInterval(() => {
                if (!window.speechSynthesis.speaking) {
                  clearInterval(iosResumeInterval);
                } else if (window.speechSynthesis.paused) {
                  window.speechSynthesis.resume();
                  console.log('iOS Safari: Resumed paused speech');
                }
              }, 300);
              
              setTimeout(() => clearInterval(iosResumeInterval), 60000);
            }
          } else {
            // Chrome/Edge/Other browsers: Direct call
            console.log('Chrome/Other: Calling speechSynthesis.speak directly');
            window.speechSynthesis.speak(utterance);
            // Verify it started
            setTimeout(() => {
              if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
                console.warn('TTS may not have started - speaking:', window.speechSynthesis.speaking, 'pending:', window.speechSynthesis.pending);
              } else {
                console.log('TTS started successfully - speaking:', window.speechSynthesis.speaking, 'pending:', window.speechSynthesis.pending);
              }
            }, 100);
          }
        } catch (error) {
          console.error('Error calling speechSynthesis.speak:', error, isSafariBrowser ? '(Safari)' : '');
          speechSynthesisRef.current = null;
          setVoiceState('idle');
        }
      };

      // Start speaking first chunk
      // Safari may need a tiny delay
      if (isSafariBrowser) {
        setTimeout(() => {
          speakChunk(0);
        }, 50);
      } else {
        speakChunk(0);
      }
    };

    // Start the speaking process
    startSpeaking();
  };

  // Stop TTS if user wants to interrupt
  const stopSpeaking = () => {
    if (window.speechSynthesis && window.speechSynthesis.speaking) {
      window.speechSynthesis.cancel();
      speechSynthesisRef.current = null;
      setVoiceState('idle');
    }
  };

  // Load voices when available (some browsers need this)
  // CRITICAL: Force voice loading on mount
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      console.log('Available TTS voices loaded:', voices.length);
      if (voices.length > 0) {
        console.log('Sample voices:', voices.slice(0, 3).map(v => v.name));
      }
    };
    
    // Try to load voices immediately
    loadVoices();
    
    // Some browsers need a small delay
    setTimeout(loadVoices, 100);
    setTimeout(loadVoices, 500);
    
    // Listen for voice changes (Chrome loads voices asynchronously)
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
    
    // Also try to trigger voice loading by calling getVoices() multiple times
    // This helps Chrome load voices faster
    const triggerVoiceLoad = () => {
      window.speechSynthesis.getVoices();
    };
    triggerVoiceLoad();
    setTimeout(triggerVoiceLoad, 200);
  }, []);

  // Function to convert URLs in text to clickable links and S3 URLs to images
  const renderMessageWithLinks = (text) => {
    if (!text) return '';
    
    // Regular expression to match S3 URLs (presigned URLs from AWS)
    const s3UrlRegex = /(https:\/\/[^\s]+\.s3[^\s]*\.amazonaws\.com[^\s]+)/g;
    // Regular expression to match other URLs
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    
    // First, split by S3 URLs to convert them to images
    const s3Parts = text.split(s3UrlRegex);
    
    return s3Parts.map((part, s3Index) => {
      // Check if this part is an S3 URL
      if (part.match(s3UrlRegex)) {
        return (
          <img
            key={`s3-${s3Index}`}
            src={part}
            alt="AED Device Image"
            className="max-w-full h-auto rounded border border-gray-300 my-2"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        );
      }
      
      // For non-S3 parts, check for regular URLs
      const urlParts = part.split(urlRegex);
      return urlParts.map((urlPart, urlIndex) => {
        if (urlPart.match(urlRegex)) {
        return (
          <a
              key={`url-${s3Index}-${urlIndex}`}
              href={urlPart}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-700 underline"
            onClick={(e) => e.stopPropagation()}
          >
              {urlPart}
          </a>
        );
      }
        return <span key={`text-${s3Index}-${urlIndex}`}>{urlPart}</span>;
      });
    });
  };

  // Auto-scroll to bottom whenever messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [messages, isWaitingForResponse]);

  // Check if API key is configured on mount
  useEffect(() => {
    const checkApiKey = async () => {
      try {
        // Use health check endpoint instead of trying to create a session
        const response = await axios.get(`${API}/health`);
        const health = response.data;
        // Check if Gemini API key is configured
        if (!health.gemini_api_key_configured && !health.openai_api_key_configured) {
          setApiKeyConfigured(false);
        } else {
          setApiKeyConfigured(true);
        }
      } catch (error) {
        // If health check fails, assume API key might be missing
        setApiKeyConfigured(false);
      }
    };
    checkApiKey();
  }, []); // API is a constant, no need to include it in dependencies

  // Load branding on mount
  useEffect(() => {
    const loadBranding = async () => {
      try {
        const response = await axios.get(`${API}/branding`);
        setBranding({
          company_name: response.data.company_name || 'AI Voice',
          logo_url: response.data.logo_url || ''
        });
      } catch (error) {
        console.error('Error loading branding:', error);
      }
    };
    loadBranding();
  }, []);


  // Initialize Gemini Live API WebSocket connection
  const initializeVoiceConnection = async () => {
    try {
      // Get auth token
      const token = localStorage.getItem('auth_token');
      if (!token) {
        toast.error('Please login first');
        return;
      }

      // Check session endpoint
      const response = await axios.post(`${API}/v1/realtime/session`, {}, {
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.data.provider || response.data.provider !== 'gemini') {
        if (!hasShownError) {
          toast.error('Gemini API not configured. Please add GOOGLE_GEMINI_API_KEY to backend .env', { duration: 5000 });
          setHasShownError(true);
        }
        return;
      }

      // Setup audio element for playback
      if (!audioElementRef.current) {
        audioElementRef.current = document.createElement('audio');
        audioElementRef.current.autoplay = true;
        document.body.appendChild(audioElementRef.current);
      }

      // Create WebSocket connection to Gemini Live API proxy
      // Convert HTTP backend URL to WebSocket URL
      console.log('BACKEND_URL from env:', BACKEND_URL);
      const backendUrl = new URL(BACKEND_URL);
      console.log('Parsed backendUrl:', backendUrl.href, 'host:', backendUrl.host);
      const wsProtocol = backendUrl.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${backendUrl.host}/api/voice/gemini/live?token=${encodeURIComponent(token)}`;
      
      console.log('Connecting to WebSocket:', wsUrl);
      console.log('Expected: ws://localhost:8000/api/voice/gemini/live?token=...');
      const ws = new WebSocket(wsUrl);
      geminiWebSocketRef.current = ws;

      ws.onopen = () => {
        console.log('Gemini Live WebSocket connected');
        console.log('WebSocket readyState:', ws.readyState, 'URL:', ws.url);
        setIsConnected(true);
        setApiKeyConfigured(true);
        toast.success('Voice connection established! Click mic to start speaking.');
      };

      ws.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received message from backend:', data);
          
          // Check for 401 authentication error
          if (data.type === 'error' && data.code === 401) {
            toast.error('Session expired. Please login again.');
            logout();
            navigate('/login');
            ws.close();
            return;
          }
          
          handleGeminiMessage(data);
        } catch (e) {
          // Handle binary audio data
          if (event.data instanceof Blob) {
            const audioBlob = new Blob([event.data], { type: 'audio/pcm' });
            const audioUrl = URL.createObjectURL(audioBlob);
            if (audioElementRef.current) {
              audioElementRef.current.src = audioUrl;
            }
          } else {
            console.error('Error parsing WebSocket message:', e, 'Raw data:', event.data);
          }
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        console.error('WebSocket error details:', {
          type: error.type,
          target: error.target,
          readyState: error.target?.readyState,
          url: error.target?.url
        });
        if (!hasShownError) {
          toast.error('Voice connection error. Check server logs for details.');
          setHasShownError(true);
        }
      };

      ws.onclose = (event) => {
        console.log('WebSocket closed', event.code, event.reason);
        console.log('WebSocket close details:', {
          code: event.code,
          reason: event.reason,
          wasClean: event.wasClean,
          url: ws.url
        });
        setIsConnected(false);
        stopAudioCapture();
        
        // Provide specific error messages based on close code
        if (event.code === 1006) {
          // Abnormal closure - connection failed before completion
          if (!hasShownError) {
            toast.error('Voice connection failed. Server may be down or unreachable.');
            setHasShownError(true);
          }
          console.error('WebSocket abnormal closure (1006) - Check if server is running and WebSocket endpoint is accessible');
        } else if (event.code === 1008) {
          // Policy violation - usually authentication
          toast.error('Authentication failed. Please login again.');
          logout();
          navigate('/login');
        } else if (event.code === 1011) {
          // Internal error
          if (!hasShownError) {
            toast.error('Server error occurred. Check server logs.');
            setHasShownError(true);
          }
          console.error('WebSocket server error (1011) - Check server logs');
        } else if (event.code === 1000) {
          // Normal closure - no error message needed
          console.log('WebSocket closed normally');
        }
      };

    } catch (error) {
      console.error('Failed to initialize voice connection:', error);
      if (!hasShownError) {
        if (error.response?.status === 503) {
          toast.error('Gemini API not configured. Add GOOGLE_GEMINI_API_KEY to backend .env', { duration: 5000 });
        } else {
          toast.error('Failed to connect. Check your microphone permissions.');
        }
        setHasShownError(true);
      }
    }
  };

  const startAudioCapture = async () => {
    try {
      const userAgent = navigator.userAgent || '';
      const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
      const isAndroid = /Android/i.test(userAgent);
      
      // Request mic permission (NewAIVoice pattern: simple getUserMedia call)
      if (!isAndroid) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          // We got permission, but we don't need to use this stream - Web Speech API will use it
          // Just close it so Web Speech API can access the mic
          stream.getTracks().forEach(track => track.stop());
          console.log('Microphone permission granted');
        } catch (micError) {
          console.error('Microphone permission error:', micError);
          if (micError.name === 'NotAllowedError' || micError.name === 'PermissionDeniedError') {
            toast.error('Microphone permission denied. Please allow microphone access in browser settings.');
            setVoiceState('idle');
            return;
          }
        }
      } else {
        console.log('Android detected - Web Speech API will handle microphone permissions');
      }
      
      // NewAIVoice pattern: Simple TTS unlock (no AudioContext needed)
      // Unlock TTS by speaking empty utterance with volume 0 during user interaction
      // Note: Unlock happens in handleMicClick, not here (to ensure it's during user interaction)
      
      // Use Web Speech API for speech-to-text (works immediately, no backend processing needed)
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        const userAgent = navigator.userAgent || '';
        const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
        const errorMsg = isMobile 
          ? 'Speech recognition not supported. Please use Chrome browser on Android.'
          : 'Speech recognition not supported in this browser. Please use Chrome or Edge.';
        toast.error(errorMsg);
        console.error('SpeechRecognition not available. window.SpeechRecognition:', window.SpeechRecognition, 'window.webkitSpeechRecognition:', window.webkitSpeechRecognition);
        setVoiceState('idle');
        return;
      }
      
      console.log('SpeechRecognition API found:', SpeechRecognition ? 'Yes' : 'No', 'User agent:', navigator.userAgent);

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      // Increase timeout to wait longer for speech
      recognition.maxAlternatives = 1;
      
      // Use component-level refs to persist transcript across recognition restarts
      // These are now defined at component level to avoid closure issues on Android Chrome
      let isListening = true;
      
      recognition.onstart = () => {
        console.log('Speech recognition started - microphone is active');
        isListening = true;
        // Reset transcripts when recognition starts/restarts
        finalTranscriptRef.current = '';
        interimTranscriptRef.current = '';
        lastCommittedFinalRef.current = '';
        lastDisplayedContentRef.current = ''; // Reset displayed content tracking for new session
        lastProcessedResultRef.current = ''; // Reset processed result tracking for new session
        messageUpdatedInThisEventRef.current = false; // Reset message update flag
        // Reset send scheduling flags
        hasScheduledSendRef.current = false;
        hasSentRef.current = false;
        lastSentTranscriptRef.current = ''; // Reset last sent transcript for new session
        // Reset intentionally stopped flag when recognition starts
        intentionallyStoppedRef.current = false;
        // Reset silence detection
        lastResultTimeRef.current = Date.now();
        if (sendTimeoutRef.current) {
          clearTimeout(sendTimeoutRef.current);
          sendTimeoutRef.current = null;
        }
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        // Clear any stale live user messages when recognition restarts to prevent duplicates
        setMessages(prev => {
          // Remove any live user messages (they might be from a previous session that didn't complete)
          const filtered = prev.filter(msg => !(msg.role === 'user' && msg.isLive));
          if (filtered.length !== prev.length) {
            console.log('Cleared stale live user messages on recognition start');
          }
          return filtered;
        });
        setVoiceState('listening'); // Ensure state is set to listening
      };
      
      recognition.onresult = (event) => {
        console.log('Speech recognition result received:', event.results.length, 'results, resultIndex:', event.resultIndex);
        
        // Reset message update flag for this event
        messageUpdatedInThisEventRef.current = false;
        
        // Update last result time for silence detection
        lastResultTimeRef.current = Date.now();
        
        // Clear any existing silence timeout since we got a new result
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        
        // Set up silence detection for EVERY result (not just final)
        // This is critical for Android Chrome where final results might not come
        const userAgentForSilence = navigator.userAgent || '';
        const isAndroidForSilence = /Android/i.test(userAgentForSilence);
        // Increased timeout to allow final words to be captured (e.g., "AED" at the end)
        // Increased to 6 seconds on Android and 7 seconds on desktop to wait longer for user to finish speaking
        const silenceTimeout = isAndroidForSilence ? 6000 : 7000; // 6 seconds on Android, 7 seconds on desktop
        
        // Set new silence timeout - this will fire if no more results come
        silenceTimeoutRef.current = setTimeout(() => {
          const finalText = finalTranscriptRef.current.trim();
          const interimText = interimTranscriptRef.current.trim();
          const currentText = finalText || interimText;
          console.log('[SILENCE DETECTION] Timeout fired!', {
            finalText,
            interimText,
            currentText,
            hasSent: hasSentRef.current,
            hasScheduledSend: hasScheduledSendRef.current,
            isProcessing: isProcessingRef.current,
            wsReady: geminiWebSocketRef.current?.readyState === WebSocket.OPEN
          });
          
          // Send if we have ANY text and haven't sent yet
          // CRITICAL: Check both flags and last sent text to prevent duplicate sends (Android)
          const currentTextNormalized = currentText.trim().toLowerCase();
          const alreadySentNormalized = lastSentTranscriptRef.current.trim().toLowerCase();
          
          // CRITICAL: If a send is scheduled (from a final result), wait for it instead of sending early
          // This prevents cutting off the last word (e.g., "AED" in "details on the unknown AED")
          if (hasScheduledSendRef.current && sendTimeoutRef.current) {
            console.log('[SILENCE DETECTION] Send already scheduled from final result, waiting for it instead of sending early');
            return; // Don't send early - let the scheduled send handle it with complete text
          }
          
          if (currentText && currentText.length > 0 && 
              !hasSentRef.current && 
              !isProcessingRef.current &&
              currentTextNormalized !== alreadySentNormalized) {
            
            console.log('Silence detected - no new results for', silenceTimeout, 'ms. Sending transcript:', currentText);
            // Use the same send logic as onend
            const ws = geminiWebSocketRef.current;
            const wsReady = ws?.readyState === WebSocket.OPEN;
            
            if (wsReady) {
              // Commit interim as final if we only have interim
              if (!finalTranscriptRef.current.trim() && interimTranscriptRef.current.trim()) {
                finalTranscriptRef.current = interimTranscriptRef.current.trim();
                lastCommittedFinalRef.current = finalTranscriptRef.current;
              }
              
              // Use final transcript if available, otherwise use interim
              let textToSend = finalTranscriptRef.current.trim();
              if (!textToSend) {
                textToSend = interimTranscriptRef.current.trim();
              }
              
              if (textToSend && textToSend.length > 0) {
                // CRITICAL: Double-check flags before sending (race condition protection)
                if (hasSentRef.current || isProcessingRef.current) {
                  console.log('Silence detection: Already sent or processing, skipping duplicate send');
                  return;
                }
                
                console.log('Silence detection: Sending message. Text:', textToSend, 'Length:', textToSend.length);
                // Mark as sent and processing IMMEDIATELY to prevent duplicates
                hasSentRef.current = true;
                isProcessingRef.current = true;
                setIsWaitingForResponse(true);
                
                const message = JSON.stringify({
                  type: 'text',
                  text: textToSend
                });
                console.log('Sending message due to silence detection:', message);
                try {
                ws.send(message);
                lastSentTranscriptRef.current = textToSend; // Track what we sent to prevent Android duplicates
                console.log('Message sent successfully from silence detection');
                } catch (sendError) {
                  console.error('Error sending in silence detection:', sendError);
                  hasSentRef.current = false;
                  isProcessingRef.current = false;
                  setIsWaitingForResponse(false);
                  return;
                }
                
                setVoiceState('processing');
                
                // Mark message as final
                setMessages(prev => {
                  const last = prev[prev.length - 1];
                  if (last && last.role === 'user' && last.isLive) {
                    return [...prev.slice(0, -1), { ...last, isLive: false }];
                  }
                  return prev;
                });
                
                // Stop recognition
                if (speechRecognitionRef.current) {
                  try {
                    speechRecognitionRef.current.stop();
                  } catch (e) {
                    // Already stopped
                  }
                }
                
                // Clear transcripts
                finalTranscriptRef.current = '';
                interimTranscriptRef.current = '';
                lastCommittedFinalRef.current = '';
                lastDisplayedContentRef.current = ''; // Clear displayed content tracking
              }
            } else {
              console.error('WebSocket not ready in silence detection:', ws?.readyState);
            }
          }
          silenceTimeoutRef.current = null;
        }, silenceTimeout);
        
        // IMPORTANT: Web Speech API behavior varies - sometimes cumulative, sometimes replaces interim
        // Strategy: Process ALL results and use the LONGEST transcript as the full sentence
        // This handles cases where API sends partial replacements instead of cumulative updates
        
        if (event.results.length === 0) return;
        
        // Get the last result (most recent update)
        const lastResult = event.results[event.results.length - 1];
        const lastTranscript = lastResult[0].transcript.trim();
        const lastIsFinal = lastResult.isFinal;
        
        // CRITICAL: Prevent processing duplicate onresult events (Android Chrome issue)
        // Create a signature from the result to detect duplicates
        const resultSignature = `${lastTranscript.toLowerCase()}_${lastIsFinal}`;
        if (resultSignature === lastProcessedResultRef.current && resultSignature.length > 0) {
          console.log('Duplicate onresult event detected, skipping:', resultSignature);
          return; // Don't process the same result twice
        }
        lastProcessedResultRef.current = resultSignature;
        
        // Log all results for debugging
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          const transcript = result[0].transcript;
          const isFinal = result.isFinal;
          console.log(`  Result ${i}: "${transcript}" (final: ${isFinal})`);
        }
        
        // ChatGPT's pattern: Web Speech API sends cumulative transcripts
        // Use the latest result directly - don't try to combine or merge
        // Find the longest transcript across all results (handles partial replacements on Android)
        let longestTranscript = lastTranscript;
        for (let i = 0; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript.trim();
          if (transcript.length > longestTranscript.length) {
            longestTranscript = transcript;
          }
        }
        
        // Use the longest transcript directly (Web Speech API is cumulative)
        // ChatGPT pattern: Final = use final, Interim = replace interim
        let displayText = longestTranscript;
        
        console.log('Using transcript for display:', displayText, '(isFinal:', lastIsFinal + ', longest:', longestTranscript + ')');
        
        if (lastIsFinal) {
          // Final result - commit to final transcript (REPLACE, not append)
          // CRITICAL: Deduplicate Android Chrome duplicate final results
          const normalizedText = displayText.trim().toLowerCase();
          const lastCommittedNormalized = lastCommittedFinalRef.current.trim().toLowerCase();
          
          if (displayText && normalizedText !== lastCommittedNormalized) {
            // CRITICAL: Check if this final is a substring of existing interim BEFORE clearing interim
            // This handles cases where final comes after interim that already includes it
            const currentInterim = interimTranscriptRef.current || '';
            if (currentInterim) {
              const interimLower = currentInterim.toLowerCase().trim();
              if (interimLower.includes(normalizedText) && interimLower.length > normalizedText.length) {
                console.log('Final is substring of interim, keeping interim instead:', displayText, 'vs', currentInterim);
                // Keep the longer interim, don't commit the shorter final
                // Don't clear interim - it's more complete
                return; // Skip this final result, keep using interim
              }
            }
            
            // New final transcript - commit it
            finalTranscriptRef.current = displayText;
            lastCommittedFinalRef.current = displayText;
            interimTranscriptRef.current = ''; // Clear interim when we get final
            console.log('Final transcript committed:', finalTranscriptRef.current);
          } else if (normalizedText === lastCommittedNormalized && normalizedText.length > 0) {
            // Android Chrome duplicate final - skip entirely
            console.log('Android duplicate final transcript detected, skipping:', displayText);
            return; // Don't update UI or schedule send if it's a duplicate
          }
        } else {
          // Interim result - update interim transcript, but keep the longest one
          // This prevents replacing longer transcripts with shorter partial replacements
          const currentInterim = interimTranscriptRef.current || '';
          const finalText = finalTranscriptRef.current || '';
          
          if (displayText.length > currentInterim.length || 
              displayText.toLowerCase().includes(currentInterim.toLowerCase()) ||
              currentInterim.length === 0) {
            interimTranscriptRef.current = displayText;
            
            // CRITICAL: If new interim includes the old final text, clear final to avoid duplication
            // This handles Android Chrome where interim often includes previous final
            if (finalText) {
              const displayLower = displayText.toLowerCase().trim();
              const finalLower = finalText.toLowerCase().trim();
              if (displayLower.includes(finalLower) && displayLower.length > finalLower.length) {
                // New interim supersedes old final - clear it to prevent "AED" + "AED status" duplication
                console.log('Interim includes final text, clearing old final to prevent duplication:', finalText);
                finalTranscriptRef.current = '';
                lastCommittedFinalRef.current = '';
                // CRITICAL: Immediately update the live message to use the new interim text
                // This prevents showing both "AED" and "AED status" as separate messages
                messageUpdatedInThisEventRef.current = true; // Mark that we're updating the message
                setMessages(prev => {
                  // First, find and update the live message
                  let hasLiveMessage = false;
                  const updated = prev.map(msg => {
                    if (msg.role === 'user' && msg.isLive) {
                      hasLiveMessage = true;
                      // Update to the new interim text that includes the old final
                      return { ...msg, content: displayText };
                    }
                    return msg;
                  });
                  
                  // CRITICAL: Remove ANY user messages (live or non-live) that are substrings of the new text
                  // This handles the case where "AED" was already committed (live or non-live)
                  // before "AED status" came as interim
                  const displayLower = displayText.toLowerCase().trim();
                  const cleaned = updated.filter((msg, idx) => {
                    if (msg.role === 'user') {
                      const msgContent = (msg.content || '').toLowerCase().trim();
                      const finalLower = finalText.toLowerCase().trim();
                      
                      // Remove messages that match the old final text exactly
                      if (msgContent === finalLower && finalLower.length > 0) {
                        console.log('Removing duplicate message matching old final:', msg.content);
                        return false;
                      }
                      
                      // Remove if new display text contains this message's content (e.g., "AED status" contains "AED")
                      if (displayLower.includes(msgContent) && 
                          displayLower.length > msgContent.length &&
                          msgContent.length > 0) {
                        console.log('Removing message that is substring of new display text:', msg.content, '->', displayText);
                        return false;
                      }
                      
                      // Remove if this message's content contains the new display text (shouldn't happen, but safety)
                      if (msgContent.includes(displayLower) && 
                          msgContent.length > displayLower.length &&
                          displayLower.length > 0) {
                        console.log('Removing message that contains new display text:', msg.content, '->', displayText);
                        return false;
                      }
                    }
                    return true;
                  });
                  
                  // If no live message exists, create one (shouldn't happen, but safety check)
                  if (!hasLiveMessage && displayText) {
                    return [...cleaned, { 
                      role: 'user', 
                      content: displayText, 
                      isLive: true, 
                      timestamp: new Date() 
                    }];
                  }
                  return cleaned;
                });
                lastDisplayedContentRef.current = displayText; // Track what we displayed
              }
            }
          } else {
            // New interim is shorter - keep the longer one
            console.log('Keeping longer interim transcript:', currentInterim, 'instead of:', displayText);
            // Use the longer one for display
            displayText = currentInterim;
          }
        }
        
        // Update UI: Follow ChatGPT's pattern - show final OR interim, never combine
        // CRITICAL: Web Speech API sends cumulative transcripts, so we should use the latest directly
        // Pattern: Final = use final, Interim = use interim (replace, don't combine)
        let combinedDisplayText;
        
        if (lastIsFinal) {
          // Final result - use it directly (ChatGPT pattern: Final = use final)
          combinedDisplayText = finalTranscriptRef.current || '';
        } else {
          // Interim result - use interim directly (ChatGPT pattern: Interim = replace)
          // Web Speech API sends cumulative transcripts, so interim already includes everything
          combinedDisplayText = interimTranscriptRef.current || '';
        }
        
        // CRITICAL: Skip normal display update if we already updated the message in interim handling
        // This prevents duplicate messages when interim includes final text
        if (combinedDisplayText && !messageUpdatedInThisEventRef.current) {
        setMessages(prev => {
            // CRITICAL: Remove ANY duplicate user messages (both live and non-live) that have similar content
            // This is the most aggressive deduplication to prevent "AED" and "AED status" appearing separately
            const normalizedNewContent = combinedDisplayText.toLowerCase().trim();
            
            // First pass: Remove duplicate live messages - we should only have ONE
            const liveMessages = prev.filter(msg => msg.role === 'user' && msg.isLive);
            if (liveMessages.length > 1) {
              console.warn('Multiple live messages detected! Removing duplicates. Count:', liveMessages.length);
              // Keep only the LAST live message (most recent), remove others
              let lastLiveIndex = -1;
              for (let i = prev.length - 1; i >= 0; i--) {
                if (prev[i].role === 'user' && prev[i].isLive) {
                  lastLiveIndex = i;
                  break;
                }
              }
              const cleaned = prev.filter((msg, idx) => {
                if (msg.role === 'user' && msg.isLive) {
                  // Keep only the last one, remove the rest
                  return idx === lastLiveIndex;
                }
                return true;
              });
              prev = cleaned;
            }
            
            // Second pass: Remove ANY user messages (both live and non-live) that are substrings
            // This is the most aggressive deduplication to prevent "AED" and "AED status" appearing separately
            prev = prev.filter((msg, idx) => {
              if (msg.role === 'user') {
                const msgContent = (msg.content || '').toLowerCase().trim();
                // Remove if new content contains this message's content (e.g., "AED status" contains "AED")
                if (normalizedNewContent.includes(msgContent) && 
                    normalizedNewContent.length > msgContent.length &&
                    msgContent.length > 0) {
                  console.log('Removing user message (live or non-live) that is substring of new content:', msg.content, '->', combinedDisplayText);
                  return false;
                }
                // Also remove if this message's content contains the new content (rare, but possible)
                if (msgContent.includes(normalizedNewContent) && 
                    msgContent.length > normalizedNewContent.length &&
                    normalizedNewContent.length > 0) {
                  console.log('Removing user message (live or non-live) that contains new content:', msg.content, '->', combinedDisplayText);
                  return false;
                }
                // Remove if messages are essentially the same (normalized comparison)
                if (msgContent === normalizedNewContent && normalizedNewContent.length > 0) {
                  console.log('Removing duplicate user message with same normalized content:', msg.content);
                  return false;
                }
              }
              return true;
            });
            
            // Find the last live user message (might not be the very last message)
            const liveMessageIndex = prev.map((msg, idx) => ({ msg, idx }))
              .filter(({ msg }) => msg.role === 'user' && msg.isLive)
              .pop();
            
            if (liveMessageIndex) {
              const last = prev[liveMessageIndex.idx];
              // Only update if content actually changed AND new text is not shorter
              // This prevents replacing longer transcripts with shorter partial replacements
              const lastContent = last.content || '';
              const newContent = combinedDisplayText;
              
              // Don't update if new text is significantly shorter (likely a partial replacement)
              // Allow update if: same length, longer, or contains previous text
              // CRITICAL: Always update if new text contains old text (e.g., "AED status" contains "AED")
              const newContentLower = newContent.toLowerCase().trim();
              const lastContentLower = lastContent.toLowerCase().trim();
              const shouldUpdate = newContent !== lastContent && (
                newContent.length >= lastContent.length ||
                newContentLower.includes(lastContentLower) || // New contains old - always update
                lastContentLower.includes(newContentLower) || // Old contains new - might be correction
                lastContent.length < 5 // Allow updates for very short texts
              );
              
              if (shouldUpdate) {
                // CRITICAL: Check if this exact content was already displayed (Android duplicate prevention)
                const normalizedNewContentForUpdate = newContent.toLowerCase().trim();
                const normalizedLastDisplayed = lastDisplayedContentRef.current.toLowerCase().trim();
                
                if (normalizedNewContentForUpdate === normalizedLastDisplayed && normalizedNewContentForUpdate.length > 0) {
                  console.log('Exact duplicate content detected in live update, skipping:', newContent);
                  return prev; // Don't update if it's the exact same content
                }
                
                // Check for duplicates in recent messages (both live and non-live)
                const recentUserMessages = prev.slice(-5).filter(msg => msg.role === 'user');
                const duplicateExists = recentUserMessages.some(msg => 
                  msg.content && 
                  msg.content.toLowerCase().trim() === newContent.toLowerCase().trim() &&
                  !msg.isLive // Only check non-live duplicates (live message is being updated)
                );
                if (duplicateExists) {
                  console.log('Duplicate transcription detected in update, skipping:', newContent);
                  return prev;
                }
                console.log('Updating live message:', lastContent, '->', newContent);
                lastDisplayedContentRef.current = newContent; // Track what we displayed
                const updated = [...prev];
                updated[liveMessageIndex.idx] = { ...last, content: newContent };
                return updated;
          } else {
                console.log('Skipping update - new text is shorter:', lastContent, '->', newContent);
                return prev;
              }
            }
            
            // Check if there's already a live message (even if content is different)
            // We should only have ONE live message at a time - ALWAYS update it, don't create new
            const hasLiveMessage = prev.some(msg => msg.role === 'user' && msg.isLive);
            if (hasLiveMessage) {
              // CRITICAL: Check if this exact content was already displayed to prevent Android duplicates
              // Reuse normalizedNewContent declared at top of callback
              const normalizedLastDisplayed = lastDisplayedContentRef.current.toLowerCase().trim();
              
              if (normalizedNewContent === normalizedLastDisplayed && normalizedNewContent.length > 0) {
                console.log('Exact duplicate content detected, skipping UI update:', combinedDisplayText);
                return prev; // Don't update if it's the exact same content
              }
              
              console.log('Live message already exists, updating it instead of creating new one');
              // Find and update the existing live message - but only if content changed
              const updated = prev.map(msg => {
                if (msg.role === 'user' && msg.isLive) {
                  // Only update if content actually changed
                  if (msg.content !== combinedDisplayText) {
                    lastDisplayedContentRef.current = combinedDisplayText; // Track what we displayed
                    return { ...msg, content: combinedDisplayText };
                  }
                }
                return msg;
              });
              return updated;
            }
            
            // No live message exists - check for duplicates in NON-LIVE messages only
            // Live messages should always be updated, not checked for duplicates
            // BUT: Always allow final results to be displayed (they need to show up when sent)
            const recentNonLiveUserMessages = prev.slice(-5).filter(msg => 
              msg.role === 'user' && !msg.isLive
            );
            const duplicateExists = recentNonLiveUserMessages.some(msg => 
              msg.content && msg.content.toLowerCase().trim() === combinedDisplayText.toLowerCase().trim()
            );
            
            // Only skip duplicates for interim results - final results should always be displayed
            if (duplicateExists && !lastIsFinal) {
              console.log('Duplicate transcription detected in non-live messages (interim), skipping:', combinedDisplayText);
              return prev;
            }
            
            // CRITICAL: Check if this exact content was already displayed (Android duplicate prevention)
            // BUT: Always allow final results to be displayed, even if they match (they need to be shown when sent)
            // Reuse normalizedNewContent declared at top of callback
            const normalizedLastDisplayed = lastDisplayedContentRef.current.toLowerCase().trim();
            
            // Only skip if it's an exact duplicate AND it's not a final result
            // Final results should always be displayed so they show up when sent
            // Note: lastIsFinal is available in this scope from the parent function
            if (normalizedNewContent === normalizedLastDisplayed && normalizedNewContent.length > 0 && !lastIsFinal) {
              console.log('Exact duplicate content detected when creating new message (interim), skipping:', combinedDisplayText);
              return prev; // Don't create if it's the exact same content (but only for interim)
            }
            
            // Only add new message if there's no live message and content doesn't already exist in non-live messages
            lastDisplayedContentRef.current = combinedDisplayText; // Track what we displayed
            return [...prev, { 
              role: 'user', 
              content: combinedDisplayText, 
              isLive: !lastIsFinal, // Final results should not be marked as live
              timestamp: new Date() 
            }];
          });
        }
      
        // If we got a new final result, schedule a send
        // On Android Chrome, wait a bit longer to ensure we capture the complete phrase
        const userAgentForDelay = navigator.userAgent || '';
        const isAndroidForDelay = /Android/i.test(userAgentForDelay);
        const sendDelay = isAndroidForDelay ? 1000 : 1500; // Increased delay to capture final words (was 500/800ms)
        
        // CRITICAL: Check if we've already sent this exact transcript (Android duplicate prevention)
        const currentFinalText = finalTranscriptRef.current.trim().toLowerCase();
        const alreadySentText = lastSentTranscriptRef.current.trim().toLowerCase();
        
        if (lastIsFinal && finalTranscriptRef.current.trim() && 
            !hasScheduledSendRef.current && 
            currentFinalText !== alreadySentText) {
          // Clear any existing timeout
          if (sendTimeoutRef.current) {
            clearTimeout(sendTimeoutRef.current);
          }
          
          // Wait a bit to see if more speech comes, then send
          const transcriptToSend = finalTranscriptRef.current.trim(); // Capture transcript at scheduling time
          hasScheduledSendRef.current = true;
          console.log(`Scheduling send after ${sendDelay}ms (Android: ${isAndroid})`);
          sendTimeoutRef.current = setTimeout(() => {
            // Reset the flag
            hasScheduledSendRef.current = false;
            sendTimeoutRef.current = null;
            
            // Check if transcript still matches (recognition might have restarted and cleared it)
            const currentTranscript = finalTranscriptRef.current.trim();
            if (!currentTranscript || currentTranscript !== transcriptToSend) {
              console.log('Transcript changed or cleared - recognition likely restarted, skipping send');
              return;
            }
            
            // Only send if recognition hasn't restarted and we still have text
            // Prevent duplicate requests - don't send if already processing
            const ws = geminiWebSocketRef.current;
            const wsReady = ws?.readyState === WebSocket.OPEN;
            console.log('WebSocket state check:', {
              hasWs: !!ws,
              readyState: ws?.readyState,
              isOpen: wsReady,
              isProcessing: isProcessingRef.current,
              transcript: currentTranscript
            });
            
            // CRITICAL: Check both flags to prevent duplicate sends
            if (currentTranscript && wsReady && !isProcessingRef.current && !hasSentRef.current) {
              const finalText = currentTranscript;
              console.log('Final result detected, sending to backend:', finalText);
              
              // Mark as processing and sent IMMEDIATELY to prevent duplicates
              hasSentRef.current = true;
              isProcessingRef.current = true;
              setIsWaitingForResponse(true);
              
              // Clear silence timeout since we're sending
              if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
                silenceTimeoutRef.current = null;
              }
              
              // Send to backend
              try {
                const message = JSON.stringify({
                  type: 'text',
                  text: finalText
                });
                console.log('Sending WebSocket message:', message);
                console.log('WebSocket object:', ws);
                console.log('WebSocket readyState before send:', ws.readyState);
                
                // Double-check WebSocket is still open before sending (Android Chrome can have timing issues)
                if (ws.readyState !== WebSocket.OPEN) {
                  console.error('WebSocket not open! State:', ws.readyState);
                  toast.error('Connection lost. Please try again.');
                  isProcessingRef.current = false;
                  setIsWaitingForResponse(false);
                  hasSentRef.current = false;
                  return;
                }
                
                ws.send(message);
                lastSentTranscriptRef.current = finalText; // Track what we sent to prevent Android duplicates
                console.log('Message sent successfully, readyState after send:', ws.readyState);
                
                // Set a timeout to detect if response doesn't come back (Android Chrome issue)
                const responseTimeout = setTimeout(() => {
                  if (isProcessingRef.current && isWaitingForResponse) {
                    console.error('No response received after 30 seconds - possible Android Chrome WebSocket issue');
                    toast.error('No response received. Please check backend logs and try again.');
                    isProcessingRef.current = false;
                    setIsWaitingForResponse(false);
                  }
                }, 30000); // 30 second timeout
                
                // Store timeout ref to clear it when response arrives
                if (!window.responseTimeouts) window.responseTimeouts = [];
                window.responseTimeouts.push(responseTimeout);
              } catch (sendError) {
                console.error('Error sending WebSocket message:', sendError);
                console.error('Error details:', {
                  name: sendError.name,
                  message: sendError.message,
                  stack: sendError.stack
                });
                toast.error('Failed to send message. Please try again.');
                isProcessingRef.current = false;
                setIsWaitingForResponse(false);
                hasSentRef.current = false;
              }
              
              setVoiceState('processing');
              // CRITICAL: Ensure user message is ALWAYS displayed when sending
              // Mark ALL live user messages as final (not live) to prevent duplicates
              // If no live message exists (due to duplicate detection), create one
              setMessages(prev => {
                const hasLiveMessages = prev.some(msg => msg.role === 'user' && msg.isLive);
                if (hasLiveMessages) {
                  console.log('Converting live message(s) to final after sending:', finalText);
                  return prev.map(msg => {
                    if (msg.role === 'user' && msg.isLive) {
                      // Convert to non-live, and update content to match what we sent
                      return { ...msg, isLive: false, content: finalText };
                    }
                    return msg;
                  });
                } else {
                  // No live message found - duplicate detection may have prevented it
                  // CRITICAL: Always add the user message when sending, even if it was detected as duplicate
                  console.log('No live message found, adding user message for sent transcript:', finalText);
                  // Check if this exact message already exists (non-live)
                  const alreadyExists = prev.some(msg => 
                    msg.role === 'user' && 
                    !msg.isLive && 
                    msg.content.toLowerCase().trim() === finalText.toLowerCase().trim()
                  );
                  if (!alreadyExists) {
                    return [...prev, { 
                      role: 'user', 
                      content: finalText, 
                      isLive: false, 
                      timestamp: new Date() 
                    }];
                  }
                }
                return prev;
              });
              
              // STOP speech recognition to prevent more transcriptions while processing
              if (speechRecognitionRef.current) {
                try {
                  speechRecognitionRef.current.stop();
                  console.log('Stopped speech recognition after sending message');
                } catch (e) {
                  console.log('Speech recognition already stopped or error stopping:', e);
                }
              }
              
              console.log('Message sent after final result, waiting for response...');
              
              // Don't clear transcripts here - let onend handle it after it checks
            } else if (isProcessingRef.current) {
              console.log('Request already in progress, skipping duplicate send');
              // Also stop recognition if already processing
              if (speechRecognitionRef.current) {
                try {
                  speechRecognitionRef.current.stop();
                  console.log('Stopped speech recognition - request already in progress');
                } catch (e) {
                  // Already stopped
                }
              }
            }
          }, sendDelay); // Wait shorter time on Android Chrome to detect end of speech faster
        }
      };
      
      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error, event);
        if (event.error === 'no-speech') {
          // No speech detected - this is normal if user hasn't spoken yet
          // Don't show error, just keep listening
          console.log('No speech detected yet - continuing to listen...');
          return;
        }
        if (event.error === 'not-allowed') {
          toast.error('Microphone permission denied. Please allow microphone access in browser settings.');
          setVoiceState('idle');
          stopMicrophone();
        } else if (event.error === 'aborted') {
          // Recognition was stopped - this is normal
          console.log('Speech recognition aborted (user stopped)');
        } else if (event.error === 'network') {
          toast.error('Network error. Please check your connection and try again.');
          setVoiceState('idle');
          stopMicrophone();
        } else if (event.error === 'service-not-allowed') {
          toast.error('Speech recognition service not available. Please try again or use text input.');
          setVoiceState('idle');
          stopMicrophone();
        } else {
          console.error(`Speech recognition error: ${event.error}`);
          // Show error for unknown errors on mobile
          if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
            toast.error(`Speech recognition error: ${event.error}. Please try again.`);
          }
        }
      };
      
      recognition.onend = async () => {
        console.log('Speech recognition ended. Final transcript:', finalTranscriptRef.current, 'Interim:', interimTranscriptRef.current);
        console.log('onend flags - hasSent:', hasSentRef.current, 'hasScheduledSend:', hasScheduledSendRef.current);
        
        // If we already sent from onresult timeout, don't send again
        if (hasSentRef.current) {
          console.log('Message already sent from onresult timeout, skipping onend send');
          // Clear transcripts now that we're done
          finalTranscriptRef.current = '';
          interimTranscriptRef.current = '';
          lastCommittedFinalRef.current = '';
          lastDisplayedContentRef.current = ''; // Clear displayed content tracking
          return; // Exit early to avoid duplicate sends
        }
        
        // If we already scheduled a send from onresult, wait a bit then send if timeout hasn't fired
        // On Android Chrome, the timeout might not fire, so we need to be more aggressive
        if (hasScheduledSendRef.current) {
          const userAgent = navigator.userAgent || '';
          const isAndroid = /Android/i.test(userAgent);
          
          if (isAndroid) {
            // On Android Chrome, if we scheduled a send but it hasn't happened yet, trigger it now
            // The timeout might not fire reliably on Android
            // BUT: Check if already sent first to prevent duplicates
            if (hasSentRef.current || isProcessingRef.current) {
              console.log('Android onend: Already sent or processing, skipping scheduled send');
              return;
            }
            console.log('Android detected - triggering scheduled send immediately from onend');
            // Clear the scheduled timeout and send now
            if (sendTimeoutRef.current) {
              clearTimeout(sendTimeoutRef.current);
              sendTimeoutRef.current = null;
            }
            hasScheduledSendRef.current = false;
            // Fall through to send the message below
          } else {
            console.log('Send already scheduled from onresult, will be handled by timeout');
            return; // Exit early to avoid duplicate sends on desktop
          }
        }
        
        // If we have a final transcript, use it. Otherwise, if we have interim text, use that.
        // This handles cases where speech ends before a final result is received
        let textToSend = finalTranscriptRef.current.trim();
        if (!textToSend && interimTranscriptRef.current.trim()) {
          // No final result yet, but we have interim text - use it
          textToSend = interimTranscriptRef.current.trim();
          console.log('No final transcript yet, using interim text:', textToSend);
          // Commit interim as final since recognition ended
          finalTranscriptRef.current = textToSend;
          lastCommittedFinalRef.current = textToSend;
        }
        
        // Send transcript to backend if we have text and haven't already scheduled a send
        const ws = geminiWebSocketRef.current;
        const wsReady = ws?.readyState === WebSocket.OPEN;
        console.log('onend WebSocket state check:', {
          hasWs: !!ws,
          readyState: ws?.readyState,
          isOpen: wsReady,
          textToSend: textToSend,
          isProcessing: isProcessingRef.current
        });
        
        // CRITICAL: Check both flags and last sent text to prevent duplicate sends (Android)
        const textToSendNormalized = textToSend.trim().toLowerCase();
        const alreadySentNormalized = lastSentTranscriptRef.current.trim().toLowerCase();
        
        if (textToSend && wsReady && 
            !hasSentRef.current && 
            !isProcessingRef.current &&
            textToSendNormalized !== alreadySentNormalized) {
            try {
              console.log('Sending transcript to backend from onend:', textToSend);
              
              // Mark as sent and processing IMMEDIATELY to prevent duplicates
              hasSentRef.current = true;
              isProcessingRef.current = true;
              setIsWaitingForResponse(true);
              
              // Mark the message as final (not live) so Gemini's transcript can replace it if needed
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last && last.role === 'user' && last.isLive) {
                  return [...prev.slice(0, -1), { ...last, isLive: false }];
                }
                return prev;
              });
              
              // Clear silence timeout since we're sending
              if (silenceTimeoutRef.current) {
                clearTimeout(silenceTimeoutRef.current);
                silenceTimeoutRef.current = null;
              }
              
              // Send text to backend for processing with Gemini
              const messageToSend = JSON.stringify({
                type: 'text',
                text: textToSend
              });
              console.log('Sending message to backend:', messageToSend);
              ws.send(messageToSend);
              lastSentTranscriptRef.current = textToSend; // Track what we sent to prevent Android duplicates
              console.log('Message sent successfully from onend');
              
              setVoiceState('processing');
              
              // STOP speech recognition to prevent more transcriptions while processing
              if (speechRecognitionRef.current) {
                try {
                  speechRecognitionRef.current.stop();
                  console.log('Stopped speech recognition after sending message');
                } catch (e) {
                  console.log('Speech recognition already stopped or error stopping:', e);
                }
              }
              
              console.log('Message sent, waiting for Gemini transcription and response...');
              
              // Clear transcripts after sending
              finalTranscriptRef.current = '';
              interimTranscriptRef.current = '';
              lastCommittedFinalRef.current = '';
              lastDisplayedContentRef.current = ''; // Clear displayed content tracking
            } catch (error) {
              console.error('Error sending transcript:', error);
              // Reset processing flags on error
              isProcessingRef.current = false;
              setIsWaitingForResponse(false);
              toast.error('Failed to send message: ' + error.message);
            }
          } else {
            if (!textToSend) {
              console.log('No speech detected - recognition ended without transcript');
            } else {
              if (!ws) {
                console.error('WebSocket not available');
                toast.error('Connection lost. Please refresh the page.');
              } else if (!wsReady) {
                console.error('WebSocket not open. State:', ws.readyState, 'Expected:', WebSocket.OPEN);
                toast.error('Connection not ready. Please try again or refresh the page.');
                // Try to reconnect
                if (ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
                  console.log('WebSocket is closed, attempting to reconnect...');
                  initializeVoiceConnection();
                }
              } else if (!textToSend) {
                console.log('No text to send');
              }
            }
          }
        
        // If still listening AND not processing AND not intentionally stopped, restart recognition
        // DO NOT restart if we're processing a request or if microphone was intentionally stopped
        if (voiceState === 'listening' && !isProcessingRef.current && !intentionallyStoppedRef.current) {
          setTimeout(() => {
            // Double-check we're still listening and not processing before restarting
            if (voiceState === 'listening' && !isProcessingRef.current && !intentionallyStoppedRef.current && speechRecognitionRef.current) {
              try {
                console.log('Restarting speech recognition...');
                // Reset transcripts and flags for new session
                finalTranscriptRef.current = '';
                interimTranscriptRef.current = '';
                lastCommittedFinalRef.current = '';
                lastDisplayedContentRef.current = ''; // Reset displayed content tracking
                hasScheduledSendRef.current = false;
                hasSentRef.current = false;
                speechRecognitionRef.current.start();
              } catch (e) {
                // Recognition already started or ended
                console.log('Recognition restart skipped:', e.message);
              }
            } else {
              console.log('Skipping recognition restart - processing request, not listening, or intentionally stopped');
            }
          }, 500); // Small delay to prevent rapid restarts
        } else if (isProcessingRef.current) {
          console.log('Skipping recognition restart - request is being processed');
        } else if (intentionallyStoppedRef.current) {
          console.log('Skipping recognition restart - microphone was intentionally stopped');
        }
      };
      
      speechRecognitionRef.current = recognition;
      
      // Start recognition with error handling
      // On Android, add a small delay to ensure Web Speech API is ready
      const startRecognition = () => {
        try {
          recognition.start();
          console.log('Speech recognition started - listening for voice input');
          setVoiceState('listening'); // Set state immediately when starting
        } catch (error) {
          console.error('Error starting speech recognition:', error);
          // On Android Chrome, recognition might already be starting
          if (error.message && (error.message.includes('already started') || error.message.includes('started'))) {
            console.log('Recognition already started, continuing...');
            setVoiceState('listening');
          } else {
            const errorMsg = isAndroid 
              ? 'Failed to start voice recognition on Android. Please check microphone permissions and try again.'
              : `Failed to start speech recognition: ${error.message || error}. Please try again.`;
            toast.error(errorMsg);
            setVoiceState('idle');
            speechRecognitionRef.current = null;
          }
        }
      };
      
      // On Android, wait a bit before starting to ensure API is ready
      if (isAndroid) {
        setTimeout(() => {
          startRecognition();
        }, 100);
      } else {
        startRecognition();
      }
      
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Microphone permission denied. Please allow microphone access in your browser settings.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No microphone found. Please connect a microphone and try again.');
      } else {
        toast.error(`Microphone error: ${error.message}`);
      }
      setVoiceState('idle');
    }
  };

  const stopAudioCapture = () => {
    console.log('Stopping audio capture...');
    if (speechRecognitionRef.current) {
      try {
        speechRecognitionRef.current.stop();
        console.log('Speech recognition stopped');
      } catch (e) {
        console.log('Error stopping recognition:', e.message);
      }
      speechRecognitionRef.current = null;
    }
    if (mediaRecorderRef.current) {
      try {
        mediaRecorderRef.current.disconnect();
      } catch (e) {
        // Already disconnected
      }
      mediaRecorderRef.current = null;
    }
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
  };

  const handleGeminiMessage = (data) => {
    const msgType = data.type;
    console.log('Handling Gemini message type:', msgType, data);
    
      if (msgType === 'response') {
      // Text response from Gemini
      const text = data.text || '';
      const images = data.images || [];
      const googleMapsLink = data.google_maps_link || null;
      console.log('Received response from Gemini:', text, 'Images:', images, 'Google Maps:', googleMapsLink);
      
      // Clear any response timeouts
      if (window.responseTimeouts) {
        window.responseTimeouts.forEach(timeout => clearTimeout(timeout));
        window.responseTimeouts = [];
      }
      
      // Stop loading state
      setIsWaitingForResponse(false);
      isProcessingRef.current = false;
      
      // Extract image URLs from text if present (format: [Images: url1, url2, ...])
      const imageUrlPattern = /\[Images:\s*([^\]]+)\]/;
      const imageMatch = text.match(imageUrlPattern);
      let extractedImages = images;
      let cleanText = text;
          
      if (imageMatch) {
        // Extract URLs from the text
        const urlString = imageMatch[1];
        extractedImages = urlString.split(',').map(url => url.trim()).filter(url => url);
        // Remove the image URL pattern from text
        cleanText = text.replace(imageUrlPattern, '').trim();
      }
      
      if (text || extractedImages.length > 0 || googleMapsLink) {
        // Stop microphone after AI response - user must click mic again to continue
        intentionallyStoppedRef.current = true; // Mark that we intentionally stopped it
        stopAudioCapture();
        isProcessingRef.current = false;
        setIsWaitingForResponse(false);
        console.log('Microphone stopped after AI response - click mic to continue');
        
        // Start TTS FIRST (oral speaking) before displaying text
        // CRITICAL iOS Safari fix: Call TTS immediately using requestAnimationFrame
        // This attempts to maintain user interaction context for iOS Safari autoplay policy
        const userAgent = navigator.userAgent || '';
        const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
        
        console.log('Starting TTS for response', { isIOSSafari, isSafari, textLength: cleanText.length, hasSpeechSynthesis: 'speechSynthesis' in window });
        
        // For iOS Safari: Use NewAIVoice pattern - simple speechSynthesis with unlock
        // TTS is unlocked on every mic click, so it should work
        if (isIOSSafari) {
          console.log('iOS Safari: Using speechSynthesis TTS (NewAIVoice pattern)');
          // Direct call - TTS should be unlocked from mic click
          speakText(cleanText);
        } else if (isSafari) {
          // Regular Safari (not iOS): Similar approach but less strict
          console.log('Regular Safari: Calling speakText');
          if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
            window.speechSynthesis.cancel();
            setTimeout(() => {
              console.log('Regular Safari: Calling speakText after cancel');
              speakText(cleanText);
            }, 100);
          } else {
            speakText(cleanText);
          }
        } else {
          // Other browsers (Chrome, Edge, etc.): Direct call works fine
          console.log('Chrome/Other browser: Calling speakText directly');
          speakText(cleanText);
        }
        
        // Then add message to UI after TTS starts
        // Small delay to ensure TTS starts first (oral speaking priority)
        setTimeout(() => {
          setMessages(prev => {
            // Check if the last message is the same (avoid duplicates)
            const lastMsg = prev[prev.length - 1];
            if (lastMsg && 
                lastMsg.role === 'assistant' && 
                lastMsg.content === cleanText &&
                JSON.stringify(lastMsg.images) === JSON.stringify(extractedImages) &&
                lastMsg.googleMapsLink === googleMapsLink) {
              console.log('Duplicate response detected, skipping');
              return prev;
            }
            return [...prev, { 
              role: 'assistant', 
              content: cleanText,
              images: extractedImages,
              googleMapsLink: googleMapsLink,
              timestamp: new Date()
            }];
          });
          console.log('Response added to messages (after TTS started)');
        }, 100); // Small delay to ensure TTS starts first (oral speaking priority)
      } else {
        console.warn('Received empty response from Gemini');
        // Still stop microphone even if response is empty
        stopAudioCapture();
        setVoiceState('idle');
        isProcessingRef.current = false;
        setIsWaitingForResponse(false);
      }
    } else if (msgType === 'transcript' || msgType === 'live_transcript') {
      // Live transcription from Gemini (real-time speech-to-text)
      // Following ChatGPT's recommendation: handle Gemini transcripts with deduplication
      // Gemini sends partial (streaming) and final transcripts, so we need to handle both
      const transcript = (data.transcript || data.text || '').trim();
      if (!transcript) return;
      
      const isFinal = data.isFinal === true || data.final === true || msgType === 'transcript';
      
        setMessages(prev => {
        // Find the last user message (should be the one we just sent)
        const lastUserIndex = prev.map((msg, idx) => ({ msg, idx }))
          .filter(({ msg }) => msg.role === 'user')
          .pop();
        
        if (lastUserIndex) {
          const lastUserMsg = prev[lastUserIndex.idx];
          
          // Deduplicate: don't update if it's the same content
          if (lastUserMsg.content && 
              lastUserMsg.content.toLowerCase().trim() === transcript.toLowerCase().trim() &&
              !lastUserMsg.isLive) {
            console.log('Duplicate Gemini transcript detected, skipping:', transcript);
            return prev;
          }
          
          // Update the last user message with Gemini's transcription
          // If it's final, mark as final; if interim, keep as live
          const updated = [...prev];
          updated[lastUserIndex.idx] = {
            ...updated[lastUserIndex.idx],
            content: transcript,
            isLive: !isFinal // Mark as final only if Gemini says it's final
          };
          return updated;
          } else {
          // No user message found, add new one (shouldn't happen normally)
          // Check for duplicates before adding
          const recentUserMessages = prev.slice(-5).filter(msg => msg.role === 'user');
          const duplicateExists = recentUserMessages.some(msg => 
            msg.content && msg.content.toLowerCase().trim() === transcript.toLowerCase().trim()
          );
          
          if (duplicateExists) {
            console.log('Duplicate Gemini transcript detected before adding, skipping:', transcript);
            return prev;
          }
          
            return [...prev, { 
            role: 'user', 
            content: transcript, 
            isLive: !isFinal,
            timestamp: new Date() 
            }];
          }
        });
    } else if (msgType === 'connected') {
      console.log('Connected to Gemini Live API');
      toast.success('Voice connection ready!');
    } else if (msgType === 'tool_call') {
      // Tool call request - handled by backend
      console.log('Tool call:', data);
    } else if (msgType === 'error') {
      const errorMsg = data.message || 'Error from Gemini';
      console.error('Error from Gemini:', errorMsg);
      setIsWaitingForResponse(false);
      isProcessingRef.current = false;
      toast.error(errorMsg);
    }
  };

  // Old handleDataChannelMessage removed - now using handleGeminiMessage for WebSocket

  const stopMicrophone = () => {
    stopAudioCapture();
  };

  const handleMicClick = async () => {
    // CRITICAL iOS Safari fix: Unlock TTS on EVERY user interaction (NewAIVoice pattern)
    // NewAIVoice unlocks on every mic click to ensure it works after first use
    const userAgent = navigator.userAgent || '';
    const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    if (isIOSSafari && window.speechSynthesis) {
      // NewAIVoice pattern: Speak empty utterance with volume 0 to unlock (every time)
      const unlockUtterance = new SpeechSynthesisUtterance('');
      unlockUtterance.volume = 0;
      window.speechSynthesis.speak(unlockUtterance);
      audioUnlockedRef.current = true;
      console.log('🔓 iOS TTS unlocked on mic click (NewAIVoice pattern)');
    }
    // Disable mic button while waiting for AI response
    if (isWaitingForResponse || isProcessingRef.current) {
      console.log('Mic button disabled - waiting for AI response');
      toast.info('Please wait for the current response to complete');
      return;
    }
    
    if (voiceMode === 'tap') {
      // Tap-to-talk mode
      if (voiceState === 'idle') {
        // Start listening
        if (!isConnected || geminiWebSocketRef.current?.readyState !== WebSocket.OPEN) {
          console.log('WebSocket not connected, initializing connection...');
          await initializeVoiceConnection();
          // Wait a bit for connection to establish
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Double-check connection
          if (geminiWebSocketRef.current?.readyState !== WebSocket.OPEN) {
            toast.error('Failed to establish connection. Please try again.');
            return;
          }
        }
        
        // Request microphone permission and start audio capture
        try {
          intentionallyStoppedRef.current = false; // Reset flag when user manually starts
          await startAudioCapture();
        setVoiceState('listening');
        toast.info('Listening... Tap mic to stop');
        } catch (error) {
          console.error('Failed to start audio capture:', error);
          toast.error('Failed to access microphone. Please check permissions.');
        }
      } else if (voiceState === 'listening') {
        // Stop listening when clicked again
        console.log('Stopping listening...');
        intentionallyStoppedRef.current = true; // Mark that user intentionally stopped it
        setVoiceState('idle');
        toast.info('Stopped listening');
        
        // Send end_speech signal to backend
        if (geminiWebSocketRef.current?.readyState === WebSocket.OPEN) {
          geminiWebSocketRef.current.send(JSON.stringify({
            type: 'end_speech'
          }));
        }
        
        // Stop the microphone
        stopMicrophone();
      } else if (voiceState === 'processing') {
        // Allow stopping during processing
        setVoiceState('idle');
        stopMicrophone();
        toast.info('Cancelled');
      } else if (voiceState === 'speaking') {
        // Stop speaking if user clicks during TTS
        stopSpeaking();
        toast.info('Stopped speaking');
      }
    }
  };

  const handleMicMouseDown = async () => {
    // Disable mic button while waiting for AI response
    if (isWaitingForResponse || isProcessingRef.current) {
      console.log('Mic button disabled - waiting for AI response');
      return;
    }
    
    if (voiceMode === 'push') {
      if (!isConnected) {
        await initializeVoiceConnection();
      }
      isPushingRef.current = true;
      setVoiceState('listening');
      toast.info('Listening... Release to send');
    }
  };

  const handleMicMouseUp = () => {
    if (voiceMode === 'push' && isPushingRef.current) {
      isPushingRef.current = false;
      setVoiceState('processing');
      toast.info('Processing...');
      setTimeout(() => setVoiceState('idle'), 1500);
    }
  };

  const handleTextQuery = async () => {
    // CRITICAL iOS Safari fix: Unlock TTS on text send (NewAIVoice pattern)
    // Unlock on every text send to ensure TTS works
    const userAgent = navigator.userAgent || '';
    const isIOSSafari = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    if (isIOSSafari && window.speechSynthesis) {
      const unlockUtterance = new SpeechSynthesisUtterance('');
      unlockUtterance.volume = 0;
      window.speechSynthesis.speak(unlockUtterance);
      audioUnlockedRef.current = true;
      console.log('🔓 iOS TTS unlocked on text send (NewAIVoice pattern)');
    }
    if (!textInput.trim()) return;
    
    // Prevent duplicate requests
    if (isProcessingRef.current) {
      console.log('Request already in progress, skipping duplicate');
      toast.info('Please wait for the current request to complete');
      return;
    }

    const userMessage = textInput;
    setTextInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage, timestamp: new Date() }]);
    setVoiceState('processing');
    
    // Mark as processing and show loading
    isProcessingRef.current = true;
    setIsWaitingForResponse(true);

    try {
      // Use voice/text-query endpoint which intelligently routes AED queries to AutoPageScraper
      const headers = getAuthHeaders();
      const response = await axios.post(`${API}/voice/text-query`, { query: userMessage }, { headers });
      console.log('Text query response:', response.data);
      const answer = response.data.answer || '';
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: answer, 
        sources: response.data.sources,
        images: response.data.images || [],
        timestamp: new Date() 
      }]);
      
      // Stop loading state
      setIsWaitingForResponse(false);
      isProcessingRef.current = false;
      setVoiceState('idle'); // Reset voice state to idle after response
      
      // Don't speak text query responses - TTS is only for voice interactions
      // speakText(answer);
    } catch (error) {
      console.error('Error querying:', error);
      // Stop loading state on error
      setIsWaitingForResponse(false);
      isProcessingRef.current = false;
      setVoiceState('idle'); // Reset voice state to idle on error too
      
      if (error.response?.status === 401) {
        // JWT expired or invalid - redirect to login
        toast.error('Session expired. Please login again.');
        logout();
        navigate('/login');
        return;
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
      toast.error('Failed to get response');
      }
      setVoiceState('idle');
    }
  };

  const getMicButtonStyle = () => {
    // Always red button
    const baseClasses = 'w-24 h-24 sm:w-28 sm:h-28 rounded-full flex items-center justify-center transition-all duration-300 touch-manipulation';
    
    // Disabled state when waiting for response or speaking - but still red
    const isDisabled = isWaitingForResponse || isProcessingRef.current || voiceState === 'speaking';
    if (isDisabled) {
      return `${baseClasses} border-2 border-red-500 bg-red-500 cursor-not-allowed opacity-70`;
    }
    
    switch (voiceState) {
      case 'listening':
        return `${baseClasses} border-2 border-red-500 text-red-500 bg-red-50`;
      case 'processing':
        return `${baseClasses} border-2 border-red-500 text-red-500 bg-red-50 animate-pulse`;
      case 'speaking':
        // Solid red background when speaking (stop speaking state)
        return `${baseClasses} border-2 border-red-500 text-white bg-red-500`;
      default:
        return `${baseClasses} border-2 border-red-500 text-red-500 active:bg-red-50`;
    }
  };

  const getMicAnimation = () => {
    switch (voiceState) {
      case 'listening':
        return { scale: [1, 1.1, 1], transition: { repeat: Infinity, duration: 1.5 } };
      case 'processing':
        return { rotate: 360, transition: { repeat: Infinity, duration: 1, ease: 'linear' } };
      default:
        return {};
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col h-screen overflow-hidden">
      {/* Header - CS logo at top */}
      <header className="bg-white px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between flex-shrink-0 border-b border-gray-200">
        <div className="flex items-center gap-3">
          {/* CS Logo - priority display */}
          <img 
            src="/CS%20AI%20Voice%20Logo.png" 
            alt="CS AI Voice Logo"
            className="h-10 sm:h-12 w-auto object-contain"
            onError={(e) => {
              // Try with spaces if URL encoded version fails
              if (e.target.src.includes('%20')) {
                e.target.src = "/CS AI Voice Logo.png";
                return;
              }
              // Fallback to branding logo if CS logo not found
              if (branding.logo_url) {
                e.target.src = branding.logo_url;
                e.target.alt = `${branding.company_name || 'Survival Path'} logo`;
              } else {
                e.target.style.display = 'none';
              }
            }}
          />
        </div>
        
        {/* User Profile & Logout */}
        <div className="flex items-center gap-3">
          {userName && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
              <User className="h-4 w-4 text-gray-600" />
              <span className="text-sm font-medium text-gray-700">{userName}</span>
            </div>
        )}
          <Button
            variant="outline"
            size="sm"
            onClick={handleLogout}
            className="flex items-center gap-2"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">Logout</span>
          </Button>
        </div>
      </header>

      {/* Main Content - Vertical Stack */}
      <div className="flex-1 flex flex-col overflow-hidden h-full px-4 sm:px-6">
        {/* Voice Interaction Area - Top Section */}
        <div className="flex flex-col items-center py-4 sm:py-6 flex-shrink-0">
          {/* API Key Warning Banner - Only show if API key is actually missing */}
          {apiKeyConfigured === false && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="w-full bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4 mb-4"
            >
              <div className="flex items-start gap-3">
                <svg className="h-5 w-5 text-yellow-600 dark:text-yellow-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300">Voice Features Disabled</h3>
                  <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                    To enable voice interaction, add your Google Gemini API key to <code className="bg-yellow-100 dark:bg-yellow-900 px-1.5 py-0.5 rounded text-xs">/app/backend/.env</code> as <code className="bg-yellow-100 dark:bg-yellow-900 px-1.5 py-0.5 rounded text-xs">GOOGLE_GEMINI_API_KEY</code> and restart the backend.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Status Display */}
          <div className="text-center space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              {voiceState === 'idle' && 'Ready to Listen'}
              {voiceState === 'listening' && 'Tap to Stop'}
              {voiceState === 'processing' && 'Thinking...'}
              {voiceState === 'speaking' && 'Speaking...'}
            </h2>
            <p className="text-sm text-gray-600">
              {voiceState === 'idle' && (voiceMode === 'push' ? 'Hold to speak' : 'Tap to start speaking')}
              {voiceState === 'listening' && 'Listening to your question...'}
              {voiceState === 'processing' && 'Processing your request'}
              {voiceState === 'speaking' && 'AI is responding'}
            </p>
          </div>

          {/* Mic Button - Outlined Red */}
          <motion.button
            data-testid="mic-trigger"
            className={getMicButtonStyle()}
            onClick={handleMicClick}
            onMouseDown={handleMicMouseDown}
            onMouseUp={handleMicMouseUp}
            onTouchStart={handleMicMouseDown}
            onTouchEnd={handleMicMouseUp}
            animate={isWaitingForResponse || isProcessingRef.current || voiceState === 'speaking' ? {} : getMicAnimation()}
            whileHover={isWaitingForResponse || isProcessingRef.current || voiceState === 'speaking' ? {} : { scale: 1.05 }}
            whileTap={isWaitingForResponse || isProcessingRef.current || voiceState === 'speaking' ? {} : { scale: 0.95 }}
            disabled={isWaitingForResponse || isProcessingRef.current || voiceState === 'speaking'}
            style={{ marginTop: '20px' }}
          >
            <Mic className={`h-10 w-10 sm:h-12 sm:w-12 ${voiceState === 'speaking' || isWaitingForResponse || isProcessingRef.current ? 'text-white' : 'text-red-500'}`} />
          </motion.button>

          {/* Text Input - Red Send Button */}
          <div className="w-full max-w-md flex gap-2 mt-6">
            <Input
              data-testid="text-input"
              type="text"
              placeholder={isWaitingForResponse || isProcessingRef.current ? "Waiting for response..." : "Or type your question..."}
              value={textInput}
              onChange={(e) => setTextInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !isWaitingForResponse && !isProcessingRef.current && handleTextQuery()}
              disabled={isWaitingForResponse || isProcessingRef.current}
              className={`flex-1 min-h-[44px] text-base border-gray-300 ${isWaitingForResponse || isProcessingRef.current ? 'opacity-50 cursor-not-allowed bg-gray-50' : ''}`}
            />
            <Button 
              onClick={handleTextQuery} 
              size="icon" 
              data-testid="send-button"
              disabled={isWaitingForResponse || isProcessingRef.current}
              className={`min-w-[44px] min-h-[44px] touch-manipulation text-white border-0 ${isWaitingForResponse || isProcessingRef.current ? 'bg-gray-400 cursor-not-allowed opacity-50' : 'bg-red-500 hover:bg-red-600'}`}
            >
              <Send className="h-5 w-5" />
            </Button>
          </div>
          </div>

        {/* Conversation Area - Always visible with black border */}
        <div className="flex-1 overflow-hidden flex flex-col mt-4 mb-4">
          <div className="border-2 border-black rounded-lg overflow-hidden flex-1 flex flex-col bg-white">
            {messages.length === 0 ? (
              <div className="flex-1 flex items-center justify-center p-6">
                <p className="text-black text-sm text-center">
                  Text conversation, requested images (Camera), etc goes down here
                </p>
              </div>
            ) : (
              <div className="overflow-y-auto overflow-x-hidden h-full p-4 space-y-3">
              <div className="flex items-center justify-between mb-2 flex-shrink-0">
                  <h3 className="text-sm font-semibold text-black">Conversation</h3>
                <button
                  onClick={() => {
                    setMessages([]);
                    setProcessedResponseIds(new Set());
                    toast.success('Conversation cleared');
                  }}
                    className="text-xs text-black hover:text-black transition-colors px-2 py-1 rounded hover:bg-gray-100"
                  data-testid="clear-conversation-button"
                >
                  Clear
                </button>
              </div>
                  <AnimatePresence>
                    {messages.map((message, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`p-3 rounded-lg ${
                          message.role === 'user' 
                          ? 'bg-blue-100 text-black' 
                          : 'bg-gray-100 text-black'
                        } ${message.isLive ? 'opacity-70' : ''}`}
                      >
                        <p className="text-sm text-black whitespace-pre-wrap break-words">
                          {renderMessageWithLinks(message.content)}
                        </p>
                      {message.images && message.images.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {message.images.map((imageUrl, imgIndex) => (
                              <div key={imgIndex} className="relative">
                                <img
                                  src={imageUrl}
                                  alt={`AED Device Image ${imgIndex + 1}`}
                                  className="w-full h-auto rounded border border-gray-300 object-contain max-h-96"
                                  onError={(e) => {
                                    console.error('Image failed to load:', imageUrl);
                                    e.target.style.display = 'none';
                                  }}
                                  onLoad={() => {
                                    console.log('Image loaded successfully:', imageUrl);
                                  }}
                                />
                </div>
                            ))}
              </div>
            </div>
          )}
                      {message.googleMapsLink && (
                        <div className="mt-3">
                          <a
                            href={message.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            View on Google Maps
                          </a>
              </div>
                      )}
                      {message.isLive && (
                        <span className="text-xs opacity-60">• Speaking...</span>
                      )}
                      {message.sources && message.sources.length > 0 && (
                        <p className="text-xs mt-2 opacity-70">
                          Sources: {message.sources.join(', ')}
                        </p>
                      )}
                    </motion.div>
                  ))}
                  {/* Loading animation when waiting for AI response */}
                  {isWaitingForResponse && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="p-3 rounded-lg bg-gray-100 text-black"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex gap-1">
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                          />
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                          />
                          <motion.div
                            className="w-2 h-2 bg-gray-400 rounded-full"
                            animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                          />
              </div>
                        <span className="text-sm text-black">AI is thinking...</span>
            </div>
          </motion.div>
        )}
                </AnimatePresence>
                <div ref={messagesEndRef} style={{ height: '1px' }} />
              </div>
            )}
            </div>
            </div>

      </div>
    </div>
  );
};

export default VoiceInterface;